import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Compass, Loader2, X, XCircle } from 'lucide-react';
import { API_STRING as api } from '@/App';

const DISMISS_KEY = 'mementa_scout_pill_dismissed';
const RECENT_MS = 60 * 60 * 1000;

// Floating top-center pill that tracks AI Scout runs across all admin pages,
// so the admin can close the scout dialog and keep working.
export const ScoutStatusPill = () => {
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [dismissedId, setDismissedId] = useState(() => localStorage.getItem(DISMISS_KEY) || '');
  const wasRunning = useRef(false);
  const timerRef = useRef(null);

  const check = async () => {
    try {
      const res = await fetch(`${api}/admin/ai/scout-active`);
      if (!res.ok) return;
      const j = await res.json();
      if (j && j.status === 'running') wasRunning.current = true;
      setJob(j && j.id ? j : null);
    } catch { /* transient */ }
  };

  useEffect(() => {
    check();
    const onStart = () => {
      wasRunning.current = true;
      check();
    };
    window.addEventListener('mementa-scout-started', onStart);
    return () => window.removeEventListener('mementa-scout-started', onStart);
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(check, (job?.status === 'running' || job?.status === 'queued') ? 4000 : 30000);
    return () => clearInterval(timerRef.current);
  }, [job?.status]);

  if (!job || !job.id) return null;

  const running = job.status === 'running' || job.status === 'queued';
  const recent = job.created_at && (Date.now() - new Date(job.created_at).getTime()) < RECENT_MS;
  const showFinished = !running && (wasRunning.current || recent) && dismissedId !== job.id;
  if (!running && !showFinished) return null;

  const dismiss = () => {
    setDismissedId(job.id);
    localStorage.setItem(DISMISS_KEY, job.id);
  };

  const cancelRun = async () => {
    try {
      await fetch(`${api}/admin/ai/scout/${job.id}/cancel`, { method: 'POST' });
      check();
    } catch { /* transient */ }
  };

  const viewDrafts = () => {
    dismiss();
    localStorage.setItem('mementa_open_drafts', '1');
    navigate('/admin/analytics?manage=products');
    window.dispatchEvent(new Event('mementa-open-drafts'));
  };

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[120] flex items-center gap-2.5 pl-4 pr-2 py-2 rounded-full shadow-lg text-sm max-w-[92vw]"
      style={{
        background: running ? '#2D4354' : job.status === 'done' ? '#166534'
          : job.status === 'cancelled' ? '#44454F' : '#7F1D1D',
        color: 'white',
        border: '1px solid rgba(254,215,165,0.5)',
      }}
      data-testid="scout-status-pill"
    >
      {running ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: '#FED7A5' }} />
          <span className="truncate" data-testid="scout-pill-progress">
            <Compass className="w-3.5 h-3.5 inline mr-1 -mt-0.5" style={{ color: '#FED7A5' }} />
            Scout working: {job.progress || 'searching...'}
          </span>
          <button
            onClick={cancelRun}
            className="px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 transition-colors hover:bg-white/25"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#FED7A5' }}
            data-testid="scout-pill-cancel-btn"
          >
            Cancel
          </button>
        </>
      ) : job.status === 'done' ? (
        <>
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#FED7A5' }} />
          <span className="truncate">
            Scout finished, {job.drafts_created || 0} new draft{(job.drafts_created || 0) === 1 ? '' : 's'} ready
          </span>
          <button
            onClick={viewDrafts}
            className="px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 transition-transform hover:scale-105"
            style={{ background: '#FED7A5', color: '#20212B' }}
            data-testid="scout-pill-view-drafts-btn"
          >
            View drafts
          </button>
        </>
      ) : job.status === 'cancelled' ? (
        <>
          <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#FED7A5' }} />
          <span className="truncate">Scout run cancelled</span>
        </>
      ) : (
        <>
          <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#FCA5A5' }} />
          <span className="truncate">Scout run failed: {job.error || 'unknown error'}</span>
        </>
      )}
      {!running && (
        <button onClick={dismiss} className="p-1 rounded-full hover:bg-white/15 flex-shrink-0" title="Dismiss" data-testid="scout-pill-dismiss-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default ScoutStatusPill;
